/**
 * E.2 — Vault → SP outbound delta worker (Graph leaf).
 *
 * Drains the vaultOutboundQueue and PUTs each pending row via the
 * existing saveBack helpers in lib/graph/sharepoint.ts. Pure async
 * function — no React, no Convex direct deps (callers pass an api
 * shim + Convex client).
 *
 * Three outcomes per row:
 *   - 200 (or 201 on initial create) → markCompleted
 *   - 412 (etag conflict)            → markFailed('conflict') →
 *                                       row transitions to
 *                                       'needs-resolution' →
 *                                       UI surfaces ConflictDialog (E.3)
 *   - 403/401                        → markFailed('access-denied')
 *   - 5xx / network                  → markFailed('transient')
 *   - other 4xx                      → markFailed('fatal')
 *
 * Drain runs N rows per invocation; the caller decides cadence (e.g.,
 * a 5-second tick + on-online + on-window-focus). Heavy lifting like
 * batching multiple PUTs per cycle is deferred to a follow-on.
 *
 * @see {@link ../writes/lanes.md} — this is lane B's PERSISTENT
 * counterpart. Lane A is for sharing PATCHes; lane B (in-memory
 * VaultSaveProvider) is the editor's debounced flush; the queue
 * here is lane B's "retry pad" so an offline blip doesn't lose work.
 */

import type { ConvexReactClient } from 'convex/react';

/** Minimal shape of the saveBack callable. The real impl lives in saveBack.ts. */
export type SaveBackFn = (input: {
  notePath: string;
  body: string;
  baseEtag: string | null;
}) => Promise<{
  status: 'ok' | 'conflict' | 'error';
  newEtag?: string;
  remoteEtag?: string;
  errorMessage?: string;
}>;

/** Resolve the body for a queued row from the client-side cache. */
export type BodyResolver = (
  pendingWriteId: string,
  bodyHash: string,
) => string | null;

export interface DrainOutboundOpts {
  /** Max rows to process per drain call. Default 5. */
  maxPerCycle?: number;
  /** Demo mode short-circuit — set if in demo. */
  showcaseSessionId?: string;
}

export interface DrainOutboundResult {
  attempted: number;
  completed: number;
  conflicts: number;
  errors: number;
  /** Per-row outcomes, suitable for logging or audit. */
  outcomes: Array<{
    queueId: string;
    notePath: string;
    outcome: 'completed' | 'conflict' | 'error' | 'no-body' | 'no-saveback';
    detail?: string;
  }>;
}

/**
 * Drain N pending rows from the outbound queue. ADDITIVE — calling
 * this never affects existing saveBack flows; it just rides on top of
 * them as a persistent retry layer.
 */
export async function drainOutbound(
  convex: ConvexReactClient,
  api: {
    vaultOutboundQueue: {
      claimNext: unknown;
      markCompleted: unknown;
      markFailed: unknown;
    };
  },
  saveBack: SaveBackFn | null,
  resolveBody: BodyResolver,
  params: { clientSlug: string },
  opts: DrainOutboundOpts = {},
): Promise<DrainOutboundResult> {
  const maxPerCycle = opts.maxPerCycle ?? 5;
  const result: DrainOutboundResult = {
    attempted: 0,
    completed: 0,
    conflicts: 0,
    errors: 0,
    outcomes: [],
  };

  for (let i = 0; i < maxPerCycle; i++) {
    const claimed = (await convex.mutation(
      api.vaultOutboundQueue.claimNext as Parameters<
        ConvexReactClient['mutation']
      >[0],
      {
        clientSlug: params.clientSlug,
        ...(opts.showcaseSessionId ? { demoSessionId: opts.showcaseSessionId } : {}),
      },
    )) as {
      _id: string;
      notePath: string;
      bodyHash: string;
      baseEtag: string | null;
    } | null;

    if (claimed === null) break; // queue empty
    result.attempted++;

    // No-body short-circuit — body cache lost (page reload). Mark as
    // fatal so the user re-saves. Without the body we can't PUT.
    const body = resolveBody(claimed._id, claimed.bodyHash);
    if (body === null) {
      await convex.mutation(
        api.vaultOutboundQueue.markFailed as Parameters<
          ConvexReactClient['mutation']
        >[0],
        {
          queueId: claimed._id,
          errorKind: 'fatal',
          errorMessage:
            'Body cache miss — re-save from the editor to re-queue.',
          ...(opts.showcaseSessionId
            ? { demoSessionId: opts.showcaseSessionId }
            : {}),
        },
      );
      result.errors++;
      result.outcomes.push({
        queueId: claimed._id,
        notePath: claimed.notePath,
        outcome: 'no-body',
      });
      continue;
    }

    // No-saveback short-circuit — useful for demo / test contexts where
    // we want to exercise the queue without a real Graph call. Marks
    // the row completed with a synthetic note so the demo desktop
    // still moves rows through the lifecycle.
    if (saveBack === null) {
      await convex.mutation(
        api.vaultOutboundQueue.markCompleted as Parameters<
          ConvexReactClient['mutation']
        >[0],
        {
          queueId: claimed._id,
          ...(opts.showcaseSessionId
            ? { demoSessionId: opts.showcaseSessionId }
            : {}),
        },
      );
      result.completed++;
      result.outcomes.push({
        queueId: claimed._id,
        notePath: claimed.notePath,
        outcome: 'no-saveback',
      });
      continue;
    }

    try {
      const saved = await saveBack({
        notePath: claimed.notePath,
        body,
        baseEtag: claimed.baseEtag,
      });
      if (saved.status === 'ok') {
        await convex.mutation(
          api.vaultOutboundQueue.markCompleted as Parameters<
            ConvexReactClient['mutation']
          >[0],
          {
            queueId: claimed._id,
            ...(opts.showcaseSessionId
              ? { demoSessionId: opts.showcaseSessionId }
              : {}),
          },
        );
        result.completed++;
        result.outcomes.push({
          queueId: claimed._id,
          notePath: claimed.notePath,
          outcome: 'completed',
        });
        continue;
      }
      if (saved.status === 'conflict') {
        await convex.mutation(
          api.vaultOutboundQueue.markFailed as Parameters<
            ConvexReactClient['mutation']
          >[0],
          {
            queueId: claimed._id,
            errorKind: 'conflict',
            errorMessage: saved.errorMessage ?? '412 Precondition Failed',
            ...(saved.remoteEtag
              ? { conflictRemoteEtag: saved.remoteEtag }
              : {}),
            ...(opts.showcaseSessionId
              ? { demoSessionId: opts.showcaseSessionId }
              : {}),
          },
        );
        result.conflicts++;
        result.outcomes.push({
          queueId: claimed._id,
          notePath: claimed.notePath,
          outcome: 'conflict',
          detail: saved.remoteEtag ?? undefined,
        });
        continue;
      }
      // saveBack returned 'error'
      const msg = saved.errorMessage ?? 'Unknown save error';
      const kind = classifyOutboundError(msg);
      await convex.mutation(
        api.vaultOutboundQueue.markFailed as Parameters<
          ConvexReactClient['mutation']
        >[0],
        {
          queueId: claimed._id,
          errorKind: kind,
          errorMessage: msg,
          ...(opts.showcaseSessionId
            ? { demoSessionId: opts.showcaseSessionId }
            : {}),
        },
      );
      result.errors++;
      result.outcomes.push({
        queueId: claimed._id,
        notePath: claimed.notePath,
        outcome: 'error',
        detail: msg,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const kind = classifyOutboundError(msg);
      await convex.mutation(
        api.vaultOutboundQueue.markFailed as Parameters<
          ConvexReactClient['mutation']
        >[0],
        {
          queueId: claimed._id,
          errorKind: kind,
          errorMessage: msg,
          ...(opts.showcaseSessionId
            ? { demoSessionId: opts.showcaseSessionId }
            : {}),
        },
      );
      result.errors++;
      result.outcomes.push({
        queueId: claimed._id,
        notePath: claimed.notePath,
        outcome: 'error',
        detail: msg,
      });
    }
  }

  return result;
}

/**
 * Map a save error message to one of the queue's errorKind values.
 * Keyword sniff for HTTP status codes embedded in the message string.
 * The dedicated taxonomy lives in future plan F-ERRORS (per
 * CURRENT-BACKLOG.md long-tail).
 */
function classifyOutboundError(
  message: string,
): 'access-denied' | 'transient' | 'fatal' {
  if (/401|403|forbidden|unauthorized|invalid_grant/i.test(message)) {
    return 'access-denied';
  }
  if (/5\d\d|network|timeout|fetch failed/i.test(message)) {
    return 'transient';
  }
  return 'fatal';
}
